import { useState } from "react";
import { api } from "../../lib/api";
import { useSite } from "../../lib/site";
import { toast } from "sonner";
import { Send, CheckCircle2 } from "lucide-react";

const inputCls = "w-full bg-white/5 border border-white/15 rounded-xl px-4 py-3 text-sm text-white placeholder:text-slate-400 outline-none focus:border-[#C9A84C] transition-colors";

export function LeadForm({ kind: initialKind = "info", propertyId, allowKinds, extra, testPrefix = "lead" }) {
  const { t, lang, types } = useSite();
  const [kind, setKind] = useState(initialKind);
  const [f, setF] = useState({ name: "", email: "", phone: "", message: "", preferred_date: "", valuation_location: "", valuation_type: "", consent: false, website: "" });
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(false);
  const set = (k) => (e) => setF({ ...f, [k]: e.target.type === "checkbox" ? e.target.checked : e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      await api.post("/public/leads", { ...f, kind, property_id: propertyId || null, lang, preferred_date: f.preferred_date || null });
      setDone(true); toast.success(t("sent_ok"));
    } catch (err) { toast.error(err?.response?.data?.detail || "Erro"); }
    setBusy(false);
  };

  if (done) return (
    <div className="flex flex-col items-center text-center gap-3 py-10" data-testid={`${testPrefix}-success`}>
      <CheckCircle2 size={44} className="text-[#C9A84C]" />
      <p className="text-slate-200">{t("sent_ok")}</p>
    </div>
  );

  return (
    <form onSubmit={submit} className="space-y-3" data-testid={`${testPrefix}-form`}>
      {allowKinds && (
        <div className="flex gap-2 p-1 bg-white/5 rounded-full w-fit">
          {allowKinds.map((k) => (
            <button type="button" key={k} onClick={() => setKind(k)} data-testid={`${testPrefix}-kind-${k}`}
              className={`px-4 py-1.5 rounded-full text-xs font-semibold transition-colors ${kind === k ? "bg-[#C9A84C] text-[#0D1B2A]" : "text-slate-300"}`}>{k === "info" ? t("info") : t("visit")}</button>
          ))}
        </div>
      )}
      <input className={inputCls} placeholder={`${t("name")} *`} required value={f.name} onChange={set("name")} data-testid={`${testPrefix}-name`} />
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
        <input className={inputCls} type="email" placeholder={t("email")} value={f.email} onChange={set("email")} data-testid={`${testPrefix}-email`} />
        <input className={inputCls} placeholder={t("phone")} value={f.phone} onChange={set("phone")} data-testid={`${testPrefix}-phone`} />
      </div>
      {kind === "avaliacao" && (
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
          <input className={inputCls} placeholder={t("val_location")} value={f.valuation_location} onChange={set("valuation_location")} data-testid={`${testPrefix}-val-location`} />
          <select className={inputCls} value={f.valuation_type} onChange={set("valuation_type")} data-testid={`${testPrefix}-val-type`}>
            <option value="" className="text-black">{t("val_type")}</option>
            {Object.entries(types).map(([k, l]) => <option key={k} value={k} className="text-black">{l}</option>)}
          </select>
        </div>
      )}
      {kind === "visita" && (
        <label className="block text-xs text-slate-400">{t("preferred_date")}
          <input type="datetime-local" className={`${inputCls} mt-1`} value={f.preferred_date} onChange={set("preferred_date")} data-testid={`${testPrefix}-date`} />
        </label>
      )}
      <textarea rows={3} className={inputCls} placeholder={t("message")} value={f.message} onChange={set("message")} data-testid={`${testPrefix}-message`} />
      <input type="text" tabIndex={-1} autoComplete="off" className="hidden" value={f.website} onChange={set("website")} aria-hidden="true" />
      {extra}
      <label className="flex items-start gap-2 text-xs text-slate-400">
        <input type="checkbox" required checked={f.consent} onChange={set("consent")} className="mt-0.5" data-testid={`${testPrefix}-consent`} /> {t("consent")}
      </label>
      <button type="submit" disabled={busy} data-testid={`${testPrefix}-submit`}
        className="w-full inline-flex items-center justify-center gap-2 bg-[#C9A84C] hover:bg-[#E0C574] text-[#0D1B2A] font-semibold rounded-xl py-3 transition-colors disabled:opacity-60">
        <Send size={15} /> {busy ? t("sending") : kind === "avaliacao" ? t("val_btn") : kind === "visita" ? t("send_visit") : t("send_info")}
      </button>
    </form>
  );
}
